import { Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { type Agente, useAdminAgenti } from "@/hooks/useAdminAgenti";
import { cn } from "@/lib/utils";

export function AdminAgentiPage() {
  const { data, isLoading } = useAdminAgenti();
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-xl font-bold tracking-tight">Agenti AI</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Gli agenti operativi della piattaforma. Ognuno ha identità, un canale e un
        referente umano a cui escalare.
      </p>

      {isLoading ? (
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.agenti ?? []).map((a) => (
            <AgenteCard key={a.slug} agente={a} onOpen={() => navigate(`/admin/agenti/${a.slug}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AgenteCard({ agente, onOpen }: { agente: Agente; onOpen: () => void }) {
  const attivo = agente.stato === "attivo";
  return (
    <button
      type="button"
      disabled={!attivo}
      onClick={onOpen}
      className={cn(
        "rounded-xl border bg-card p-4 text-left transition-all",
        attivo ? "hover:-translate-y-0.5 hover:border-brand-700 hover:shadow-sm" : "opacity-60",
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "relative grid size-11 shrink-0 place-items-center rounded-xl",
            attivo ? "bg-brand-50" : "bg-muted",
          )}
        >
          <Bot className={cn("size-6", attivo ? "text-brand-700" : "text-slate-400")} />
          <span className="absolute -bottom-1 -right-1 rounded border-2 border-card bg-slate-900 px-1 text-[8px] font-bold text-white">
            AI
          </span>
        </div>
        <div className="min-w-0">
          <div className="font-bold">{agente.nome}</div>
          <div className="truncate text-xs text-muted-foreground">
            {agente.ruolo} · {agente.modulo}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs font-semibold">
            <span
              className={cn(
                "size-2 rounded-full",
                attivo ? "bg-emerald-500" : "bg-slate-300",
              )}
            />
            {attivo
              ? `Attivo${agente.referente ? ` · referente ${agente.referente}` : ""}`
              : "In arrivo"}
          </div>
        </div>
      </div>

      {attivo && agente.metriche && (
        <div className="mt-4 flex gap-5 border-t pt-3.5">
          <Stat n={agente.metriche.email_7gg} l="email · 7gg" />
          <Stat n={agente.metriche.in_errore} l="in errore" />
          <Stat n={`$${agente.metriche.costo_ai_7gg_usd.toFixed(2)}`} l="costo AI 7gg" />
        </div>
      )}
    </button>
  );
}

function Stat({ n, l }: { n: number | string; l: string }) {
  return (
    <div>
      <div className="text-[17px] font-bold tabular-nums">{n}</div>
      <div className="text-[11px] text-muted-foreground">{l}</div>
    </div>
  );
}
